import React, { useState, useEffect } from 'react';
import {
  DataTable,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Pagination,
  Search,
  SkeletonText,
} from '@carbon/react';
import '@carbon/styles/css/styles.css';
import './facilityDataTableComponent.css';

export const FacilityDataTableComponent = ({ district, dateFrom, dateTo }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const controller = new AbortController();

    async function fetchData() {
      setLoading(true);
      setError(null);

      try {
        const isAllDistricts = district?.trim().toUpperCase() === 'ALL';
        const params = new URLSearchParams(
          isAllDistricts
            ? { cmd: 'getAllServicesByDistrict_' }
            : {
                cmd: 'getAllServicesByDistrict',
                district,
                date_from: dateFrom,
                date_to: dateTo,
              }
        );
        const response = await fetch(
          `https://sgbv.caringsoulshomecare.com/api/?${params}`,
          { signal: controller.signal }
        );

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const data = await response.json();

        if (data.status === 'ok' && Array.isArray(data.results)) {
          const filteredResults = data.results.filter(item => item.duty_station_type === '0');
          const formattedRows = filteredResults.map((item, index) => ({
            id: (index + 1).toString(),
            duty_station: item.duty_station,
            HTS_testing_count: item.HTS_testing_count ?? 'N/A',
            PEP_count: item.PEP_count ?? 'N/A',
            Emergency_Contraceptives_count: item.Emergency_Contraceptives_count ?? 'N/A',
            Trauma_Counseling_count: item.Trauma_Counseling_count ?? 'N/A',
            Other_specify_count: item.Other_specify_count ?? 'N/A',
          }));
          setRows(formattedRows);
          setCurrentPage(1);
        } else if (data.status === 'empty') {
          setRows([]);
          setCurrentPage(1);
        } else {
          throw new Error(data.message || 'No data available');
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          setRows([]);
          setError(error);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }

    if (district && dateFrom && dateTo) {
      fetchData();
    }

    return () => controller.abort();
  }, [district, dateFrom, dateTo]);

  const headers = [
    {
      key: 'duty_station',
      header: 'Facility',
    },
    {
      key: 'HTS_testing_count',
      header: 'HTS Testing',
    },
    {
      key: 'PEP_count',
      header: 'PEP',
    },
    {
      key: 'Emergency_Contraceptives_count',
      header: 'Emergency Contraceptives',
    },
    {
      key: 'Trauma_Counseling_count',
      header: 'Trauma Counseling',
    },
    {
      key: 'Other_specify_count',
      header: 'Other specify',
    },
  ];

  const filteredRows = rows.filter(row =>
    row.duty_station?.toLowerCase().includes(searchTerm.trim().toLowerCase())
  );

  const getCurrentPageRows = () => {
    const start = (currentPage - 1) * rowsPerPage;
    const end = start + rowsPerPage;
    return filteredRows.slice(start, end);
  };

  if (loading) {
    return <div className="table-state"><SkeletonText paragraph lineCount={4} /></div>;
  }

  if (error) {
    return <div className="table-state table-error" role="alert">Unable to load facility services: {error.message}</div>;
  }

  return (
    <div className="data-table-container">
      <div className="table-search">
        <Search
          id="facility-search"
          labelText="Search health facilities"
          placeholder="Search facilities"
          size="lg"
          value={searchTerm}
          onChange={event => {
            setSearchTerm(event.target.value);
            setCurrentPage(1);
          }}
        />
        <span>{filteredRows.length} {filteredRows.length === 1 ? 'facility' : 'facilities'}</span>
      </div>
      {filteredRows.length === 0 ? (
        <div className="table-empty">No health facilities match the current filters.</div>
      ) : (
      <div className="table-scroll-area">
      <DataTable rows={getCurrentPageRows()} headers={headers}>
        {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
          <Table {...getTableProps()}>
            <TableHead>
              <TableRow>
                {headers.map((header) => (
                  <TableHeader {...getHeaderProps({ header })}>
                    {header.header}
                  </TableHeader>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow {...getRowProps({ row })}>
                  {row.cells.map((cell) => (
                    <TableCell key={cell.id}>{cell.value}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DataTable>
      <Pagination
        totalItems={filteredRows.length}
        pageSize={rowsPerPage}
        pageSizes={[5, 10, 15, 20]}
        onChange={({ page, pageSize }) => {
          setCurrentPage(page);
          setRowsPerPage(pageSize);
        }}
      />
      </div>
      )}
    </div>
  );
};
